/**
 * Página de Detalle de Miembro
 * Muestra información completa de una persona y sus membresías
 */

import { useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Button,
  Badge,
} from "@/components/ui";
import {
  ArrowLeft,
  User,
  Mail,
  Phone,
  MapPin,
  Calendar,
  Heart,
  Shield,
  Edit,
  Users,
  UserPlus,
  RefreshCw,
  ArrowRightLeft,
  ClipboardList,
  Loader2,
  AlertCircle,
  Award,
  BookOpen,
} from "lucide-react";
import { usePerson, useMembersByPerson } from "@/hooks/usePersons";
import { CLUB_TYPE_LABELS } from "@/types";
import PersonModal from "./components/PersonModal";
import EnrollMemberModal from "./components/EnrollMemberModal";
import TransferMemberModal from "@/components/modals/TransferMemberModal";
import { usePermissions } from "@/hooks/usePermissions";
import { StatusChangeModal } from "./components/StatusChangeModal";
import { AttendanceHistoryModal } from "./components/AttendanceHistoryModal";
import DocumentsCard from "@/pages/profile/components/DocumentsCard";
import { Breadcrumbs } from "@/components/ui/Breadcrumbs";
import type { MemberLegacy as Member } from "@/types";

const documentTypeLabels: Record<string, string> = {
  CC: "Cédula de Ciudadanía",
  TI: "Tarjeta de Identidad",
  RC: "Registro Civil",
  CE: "Cédula de Extranjería",
  PASAPORTE: "Pasaporte",
  PPT: "Permiso por Protección Temporal (PPT)",
};

const genderLabels: Record<string, string> = {
  M: "Masculino",
  F: "Femenino",
};

const statusLabels: Record<string, string> = {
  active: "Activo",
  inactive: "Inactivo",
};

const statusColors: Record<string, string> = {
  active: "bg-green-100 text-green-800",
  inactive: "bg-gray-100 text-gray-800",
};

const memberStatusLabels: Record<string, string> = {
  active: "Activo",
  inactive: "Inactivo",
  pending: "Pendiente",
  transferred: "Transferido",
};

const memberStatusColors: Record<string, string> = {
  active: "bg-green-100 text-green-800",
  inactive: "bg-gray-100 text-gray-800",
  pending: "bg-yellow-100 text-yellow-800",
  transferred: "bg-blue-100 text-blue-800",
};

export default function MemberDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data: person, isLoading, error } = usePerson(id!);
  const { data: memberships } = useMembersByPerson(id!);

  const [showEditModal, setShowEditModal] = useState(false);
  const [showEnrollModal, setShowEnrollModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showAttendanceModal, setShowAttendanceModal] = useState(false);
  const [selectedMember, setSelectedMember] = useState<Member | null>(null);
  // F11: traslado de membresía a otro club (solo admins)
  const [transferMember, setTransferMember] = useState<Member | null>(null);
  const { isUnionAdmin, isAssociationAdmin } = usePermissions();
  const isAdminUser = isUnionAdmin || isAssociationAdmin;

  const formatDate = (
    timestamp: { toDate?: () => Date } | Date | null | undefined,
  ) => {
    if (!timestamp) return "-";
    const date =
      typeof timestamp === "object" && "toDate" in timestamp && timestamp.toDate
        ? timestamp.toDate()
        : new Date(timestamp as unknown as string | number | Date);
    return date.toLocaleDateString("es-CO", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const calculateAge = (
    birthDate: { toDate?: () => Date } | Date | null | undefined,
  ) => {
    if (!birthDate) return "-";
    const date =
      typeof birthDate === "object" && "toDate" in birthDate && birthDate.toDate
        ? birthDate.toDate()
        : new Date(birthDate as unknown as string | number | Date);
    const today = new Date();
    let age = today.getFullYear() - date.getFullYear();
    const monthDiff = today.getMonth() - date.getMonth();
    if (
      monthDiff < 0 ||
      (monthDiff === 0 && today.getDate() < date.getDate())
    ) {
      age--;
    }
    return `${age} años`;
  };

  const handleChangeStatus = (member: Member) => {
    setSelectedMember(member);
    setShowStatusModal(true);
  };

  const handleViewAttendance = (member: Member) => {
    setSelectedMember(member);
    setShowAttendanceModal(true);
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  // Error state
  if (error || !person) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-center">
        <AlertCircle className="w-12 h-12 text-red-400 mb-4" />
        <h2 className="text-xl font-semibold text-gray-900 mb-2">
          Persona no encontrada
        </h2>
        <p className="text-gray-500 mb-4">
          No se pudo cargar la información de esta persona.
        </p>
        <Button onClick={() => navigate("/members")}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Volver a miembros
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Breadcrumbs
        items={[
          { label: "Miembros", to: "/members" },
          { label: person.fullName },
        ]}
      />
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button
            variant="secondary"
            onClick={() => navigate("/members")}
            className="!p-2"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <User className="w-7 h-7 text-primary-600" />
              {person.fullName}
            </h1>
            <p className="text-gray-500 mt-1">
              {documentTypeLabels[person.idDocumentType] ||
                person.idDocumentType}
              : {person.idDocumentNumber}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            variant="secondary"
            onClick={() => setShowEnrollModal(true)}
            className="flex items-center gap-2"
          >
            <UserPlus className="w-4 h-4" />
            Inscribir en Club
          </Button>
          <Button
            onClick={() => setShowEditModal(true)}
            className="flex items-center gap-2"
          >
            <Edit className="w-4 h-4" />
            Editar
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Profile Card */}
        <Card className="border-0 shadow-lg">
          <CardContent className="pt-6">
            <div className="text-center">
              {person.photoUrl ? (
                <img
                  src={person.photoUrl}
                  alt={person.fullName}
                  className="w-32 h-32 rounded-full object-cover mx-auto mb-4 border-4 border-white shadow-lg"
                />
              ) : (
                <div className="w-32 h-32 rounded-full bg-gradient-to-br from-primary-400 to-primary-600 flex items-center justify-center mx-auto mb-4 shadow-lg">
                  <span className="text-4xl font-bold text-white">
                    {person.firstName?.charAt(0)}
                    {person.lastName?.charAt(0)}
                  </span>
                </div>
              )}
              <h2 className="text-xl font-semibold text-gray-900">
                {person.fullName}
              </h2>
              <p className="text-gray-500">
                {genderLabels[person.gender] || person.gender}
              </p>
              <Badge className={`mt-2 ${statusColors[person.status]}`}>
                {statusLabels[person.status] || person.status}
              </Badge>
            </div>

            <div className="mt-6 space-y-3">
              {person.email && (
                <div className="flex items-center gap-3 text-sm">
                  <Mail className="w-4 h-4 text-gray-400" />
                  <span className="text-gray-600">{person.email}</span>
                </div>
              )}
              {person.phone && (
                <div className="flex items-center gap-3 text-sm">
                  <Phone className="w-4 h-4 text-gray-400" />
                  <span className="text-gray-600">{person.phone}</span>
                </div>
              )}
              {person.address && (
                <div className="flex items-center gap-3 text-sm">
                  <MapPin className="w-4 h-4 text-gray-400" />
                  <span className="text-gray-600">{person.address}</span>
                </div>
              )}
              <div className="flex items-center gap-3 text-sm">
                <Calendar className="w-4 h-4 text-gray-400" />
                <span className="text-gray-600">
                  {formatDate(person.birthDate)} (
                  {calculateAge(person.birthDate)})
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Info Cards */}
        <div className="lg:col-span-2 space-y-6">
          {/* Información Personal */}
          <Card className="border-0 shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="w-5 h-5 text-primary-600" />
                Información Personal
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Nombre Completo</p>
                  <p className="font-medium">{person.fullName}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Documento</p>
                  <p className="font-medium">
                    {documentTypeLabels[person.idDocumentType]} -{" "}
                    {person.idDocumentNumber}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Fecha de Nacimiento</p>
                  <p className="font-medium">{formatDate(person.birthDate)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Edad</p>
                  <p className="font-medium">
                    {calculateAge(person.birthDate)}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Género</p>
                  <p className="font-medium">{genderLabels[person.gender]}</p>
                </div>
                {person.address && (
                  <div>
                    <p className="text-sm text-gray-500">Dirección</p>
                    <p className="font-medium">{person.address}</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Información Médica */}
          <Card className="border-0 shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Heart className="w-5 h-5 text-red-500" />
                Información Médica
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Tipo de Sangre</p>
                  <p className="font-medium">
                    {person.bloodType || "No registrado"}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Seguro de Salud</p>
                  <p className="font-medium">
                    {person.healthInsurance || "No registrado"}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Contacto de Emergencia */}
          {person.emergencyContact && (
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="w-5 h-5 text-blue-500" />
                  Contacto de Emergencia
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Nombre</p>
                    <p className="font-medium">
                      {person.emergencyContact.name || "-"}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Parentesco</p>
                    <p className="font-medium">
                      {person.emergencyContact.relationship || "-"}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Teléfono</p>
                    <p className="font-medium">
                      {person.emergencyContact.phone || "-"}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Membresías */}
          <Card className="border-0 shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-primary-600" />
                  Membresías en Clubes
                </span>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setShowEnrollModal(true)}
                  className="flex items-center gap-1"
                >
                  <UserPlus className="w-4 h-4" />
                  Inscribir
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {!memberships || memberships.length === 0 ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-gradient-to-br from-gray-200 to-gray-300 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                    <Users className="w-8 h-8 text-gray-500" />
                  </div>
                  <p className="text-gray-500 font-medium">
                    Esta persona no está inscrita en ningún club
                  </p>
                  <Button
                    variant="secondary"
                    onClick={() => setShowEnrollModal(true)}
                    className="mt-4"
                  >
                    Inscribir en un club
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {memberships.map((membership) => (
                    <div
                      key={membership.id}
                      className="border-2 border-gray-100 rounded-xl p-4"
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="flex items-center gap-2">
                            <h4 className="font-medium text-gray-900">
                              {(() => {
                                const sec =
                                  membership.section ?? membership.clubType;
                                return sec
                                  ? CLUB_TYPE_LABELS[sec]
                                  : "Sin sección";
                              })()}
                            </h4>
                            <Badge
                              className={memberStatusColors[membership.status]}
                            >
                              {memberStatusLabels[membership.status]}
                            </Badge>
                          </div>
                          <p className="text-sm text-gray-500 mt-1">
                            {membership.position &&
                              `Cargo: ${membership.position}`}
                          </p>
                          <p className="text-xs text-gray-400 mt-1">
                            Desde: {formatDate(membership.registrationDate)}
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleViewAttendance(membership)}
                            className="p-2 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-xl transition-colors duration-200"
                            title="Ver asistencia"
                          >
                            <ClipboardList className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleChangeStatus(membership)}
                            className="p-2 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-xl transition-colors duration-200"
                            title="Cambiar estado"
                          >
                            <RefreshCw className="w-4 h-4" />
                          </button>
                          {isAdminUser && (
                            <button
                              onClick={() => setTransferMember(membership)}
                              className="p-2 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded-xl transition-colors duration-200"
                              title="Trasladar de club"
                            >
                              <ArrowRightLeft className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Documentos anexos */}
          <DocumentsCard person={person} />

          {/* Logros y Progreso */}
          <Card className="border-0 shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Award className="w-5 h-5 text-yellow-500" />
                Logros y Progreso
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-gradient-to-br from-yellow-100 to-yellow-200 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                  <BookOpen className="w-8 h-8 text-yellow-500" />
                </div>
                <p className="text-gray-500 font-medium">
                  Próximamente: clases, especialidades e investiduras
                </p>
                <Link to="/progress">
                  <Button variant="secondary" className="mt-4">
                    Ir a Progreso
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Modales */}
      <PersonModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        person={person}
      />

      {transferMember && (
        <TransferMemberModal
          isOpen
          onClose={() => setTransferMember(null)}
          member={transferMember}
        />
      )}

      <EnrollMemberModal
        isOpen={showEnrollModal}
        onClose={() => setShowEnrollModal(false)}
        person={person}
      />

      {selectedMember && (
        <>
          <StatusChangeModal
            open={showStatusModal}
            onOpenChange={(open) => {
              setShowStatusModal(open);
              if (!open) setSelectedMember(null);
            }}
            member={selectedMember}
            personName={person.fullName}
          />
          <AttendanceHistoryModal
            isOpen={showAttendanceModal}
            onClose={() => {
              setShowAttendanceModal(false);
              setSelectedMember(null);
            }}
            member={selectedMember}
            personName={person.fullName}
          />
        </>
      )}
    </div>
  );
}
